import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";

import type { RateProviderUsage } from "../../types/rateProviderUsage.types";

interface ProviderErrorsPanelProps {
  providers: RateProviderUsage[];
  formatCount: (value: number) => string;
  formatDateTime: (iso: string) => string;
}

/** `{}` is what the backend stores when the provider answered nothing parseable; it is not worth a row. */
const hasBody = (value: Record<string, unknown> | undefined): value is Record<string, unknown> =>
  value !== undefined && value !== null && Object.keys(value).length > 0;

/**
 * Why the failing providers failed, for the selected month.
 *
 * The grouped code answers whether the spend is one bad deploy or a standing condition. The last error then
 * has to say WHICH, and the code alone never does: an `http_404` is the request never reaching an endpoint,
 * which is a base URL to fix, while an `http_401` is a credential to rotate and an `http_429` is the plan
 * running out. So the called URL and the provider's own body are rendered verbatim next to it — the first
 * `404` here was a base URL missing its version segment, invisible from the code and obvious from the URL.
 *
 * Neither field can carry a secret. `full_url` is base + path with the query string left off, and every rate
 * client passes its credentials as request params or as an auth header, never in the path; the backend builds
 * the detail from the exception's response data and deliberately excludes its request body.
 *
 * Providers that answered everything are omitted: an empty panel is the good state.
 */
export const ProviderErrorsPanel = ({ providers, formatCount, formatDateTime }: ProviderErrorsPanelProps) => {
  const { t } = useTranslations("rateProviderUsage");
  const failing = providers.filter((provider) => provider.errors_by_code.length > 0);

  if (failing.length === 0) {
    return (
      <ColocaSurface className="p-6 text-center">
        <p className="text-sm text-muted-foreground">{t("errors_none")}</p>
      </ColocaSurface>
    );
  }

  return (
    <div className="space-y-3">
      {failing.map((provider) => (
        <ColocaSurface key={provider.provider_code} className="space-y-3 p-4">
          <p className="font-mono text-sm font-medium">{provider.provider_code}</p>

          <ul className="space-y-1.5">
            {provider.errors_by_code.map((group) => (
              <li
                key={`${group.error_code ?? "unknown"}:${group.http_status ?? "none"}`}
                className="flex flex-wrap items-baseline justify-between gap-2 text-sm"
              >
                <span className="font-mono text-xs">
                  {group.error_code ?? t("ladder_error_unknown")}
                  {group.http_status !== null ? (
                    <span className="ml-1 text-muted-foreground">
                      {t("errors_http_status", { status: group.http_status })}
                    </span>
                  ) : null}
                </span>
                <span className="tabular-nums text-muted-foreground">
                  {t("errors_occurrences", { value: formatCount(group.count) })}
                </span>
              </li>
            ))}
          </ul>

          {provider.last_error ? (
            <div className="space-y-2 border-t pt-3 text-xs text-muted-foreground">
              <p className="font-medium">
                {t("errors_last_at", { at: formatDateTime(provider.last_error.at) })}
              </p>

              <dl className="space-y-1.5">
                {provider.last_error.detail?.message ? (
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                    <dt className="shrink-0 sm:w-24">{t("errors_detail_message")}</dt>
                    <dd className="font-mono break-all">{provider.last_error.detail.message}</dd>
                  </div>
                ) : null}

                {provider.last_error.detail?.full_url ? (
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                    <dt className="shrink-0 sm:w-24">{t("errors_detail_url")}</dt>
                    <dd className="font-mono break-all">{provider.last_error.detail.full_url}</dd>
                  </div>
                ) : null}

                {hasBody(provider.last_error.detail?.response_data) ? (
                  <div className="flex flex-col gap-1">
                    <dt>{t("errors_detail_response")}</dt>
                    <dd>
                      <pre className="max-h-40 overflow-auto rounded-md border bg-muted/40 p-2 font-mono text-xs whitespace-pre-wrap break-all">
                        {JSON.stringify(provider.last_error.detail.response_data, null, 2)}
                      </pre>
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
          ) : null}
        </ColocaSurface>
      ))}
    </div>
  );
};
